import {
  ArrowRight,
  CheckCircle2,
  Copy,
  Eye,
  RefreshCw,
  RotateCcw,
  Zap,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "@/components/ui/Button";
import type { Installment, InstallmentStatus } from "@/types/installment";
import type { Mandate } from "@/types/mandate";
import { useToast } from "@/components/ui/Toast";
import { dayName, formatDueDate, formatINR, MONTHS } from "@/pages/retailer/installments/data";
import { installmentService } from "@/services/apiServices";
import InstallmentStatusBadge from "@/pages/retailer/installments/components/InstallmentStatusBadge";
import RetryModal from "@/pages/retailer/installments/components/RetryModal";
import SkipModal from "@/pages/retailer/installments/components/SkipModal";
import AdminInstallmentDetailModal from "@/pages/admin/installments/components/InstallmentDetailModal";
import { useInstallmentLiveUpdates } from "@/context/RealtimeEventsContext";

const COLLECTED: InstallmentStatus[] = ["COLLECTED", "SETTLEMENT_PENDING", "SETTLED", "SETTLEMENT_FAILED"];

interface Props {
  mandate: Mandate;
}

export default function AdminInstallmentSchedule({ mandate }: Props) {
  const navigate = useNavigate();
  const toast = useToast();
  const [rows, setRows] = useState<Installment[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [refreshingId, setRefreshingId] = useState<string | null>(null);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailTarget, setDetailTarget] = useState<Installment | null>(null);

  const [retryOpen, setRetryOpen] = useState(false);
  const [retryTarget, setRetryTarget] = useState<Installment | null>(null);
  const [retryLoading, setRetryLoading] = useState(false);

  const [skipOpen, setSkipOpen] = useState(false);
  const [skipTarget, setSkipTarget] = useState<Installment | null>(null);
  const [skipLoading, setSkipLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await installmentService.getInstallments({ refNo: mandate.refNo, limit: 500, sort: "Due Date" });
      setRows(res?.data?.installments ?? []);
    } catch (_err) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [mandate.refNo]);

  useEffect(() => {
    load();
  }, [load]);

  // Real-time live update listener from Gateway Webhooks / SSE
  useInstallmentLiveUpdates((payload) => {
    if (payload?.installment) {
      const inst = payload.installment;
      setRows((prev) => {
        const idx = prev.findIndex(
          (r) =>
            r.id === inst.id ||
            (inst.rocketpayInstallmentId && r.rocketpayInstallmentId === inst.rocketpayInstallmentId) ||
            r.installmentNumber === inst.installmentNumber
        );
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = { ...next[idx], ...inst };
          return next;
        }
        return [...prev, inst];
      });
      setDetailTarget((prev) => (prev && (prev.id === inst.id || prev.installmentNumber === inst.installmentNumber) ? { ...prev, ...inst } : prev));
      toast.success(`Live Update: Installment #${inst.installmentNumber || ''} is now ${inst.status}`);
    } else {
      load();
    }
  }, mandate.refNo);

  async function handleSyncRocketPay() {
    setSyncing(true);
    try {
      await installmentService.getByMandate(mandate.refNo);
      await load();
      toast.success("Schedule synchronized successfully");
    } catch (err: any) {
      toast.error(err?.message || "Failed to sync schedule");
    } finally {
      setSyncing(false);
    }
  }

  async function handleRefreshRow(row: Installment) {
    setRefreshingId(row.id);
    try {
      let fresh: Installment | undefined;
      try {
        const rpRes = await installmentService.refresh(row.id);
        fresh = rpRes?.data?.installment;
      } catch (_rpErr) {
        const res = await installmentService.getById(row.id);
        fresh = res?.data?.installment;
      }
      if (fresh) {
        setRows((prev) => prev.map((r) => (r.id === fresh!.id ? fresh! : r)));
        setDetailTarget((prev) => (prev && prev.id === fresh!.id ? fresh! : prev));
        toast.success(`Installment #${row.installmentNumber} synchronized: ${fresh.status}`);
      }
    } catch (err: any) {
      toast.error(err?.message || "Could not refresh installment");
    } finally {
      setRefreshingId(null);
    }
  }

  async function handleRetryConfirm(retryDate: string) {
    if (!retryTarget) return;
    setRetryLoading(true);
    try {
      let schedule_date = retryDate;
      if (retryDate && retryDate.includes(" ")) {
        const parts = retryDate.split(" ");
        if (parts.length === 3) {
          const monthIdx = MONTHS.indexOf(parts[1]) + 1;
          if (monthIdx > 0) {
            schedule_date = `${parts[2]}-${String(monthIdx).padStart(2, "0")}-${parts[0].padStart(2, "0")}`;
          }
        }
      }
      const res = await installmentService.retry(retryTarget.id, schedule_date, retryDate);
      toast.success(res?.message || "Retry scheduled successfully");
      setRetryOpen(false);
      setRetryTarget(null);
      await load();
    } catch (err: any) {
      toast.error(err?.message || "Could not schedule retry");
    } finally {
      setRetryLoading(false);
    }
  }

  async function handleSkipConfirm(reason: string, remarks: string) {
    if (!skipTarget) return;
    setSkipLoading(true);
    try {
      const res = await installmentService.skip(skipTarget.id, remarks ? `${reason} — ${remarks}` : reason);
      toast.success(res?.message || "Installment skipped successfully");
      setSkipOpen(false);
      setSkipTarget(null);
      await load();
    } catch (err: any) {
      toast.error(err?.message || "Could not skip installment");
    } finally {
      setSkipLoading(false);
    }
  }

  const collected = rows.filter((r) => COLLECTED.includes(r.status) || r.status === "COLLECTED_OFFLINE");
  const collectedCount = collected.length;
  const totalCount = rows.length;
  const collectedAmt = collected.reduce((s, r) => s + r.amount, 0);
  const totalAmt = rows.reduce((s, r) => s + r.amount, 0);

  const nextDue = useMemo(() => {
    const scheduled = rows
      .filter((r) => r.status === "SCHEDULED")
      .sort((a, b) => a.dueDate.localeCompare(b.dueDate));
    if (scheduled.length) return scheduled[0];
    const presented = rows
      .filter((r) => r.status === "PRESENTED")
      .sort((a, b) => a.dueDate.localeCompare(b.dueDate));
    return presented.length ? presented[0] : null;
  }, [rows]);

  const progress = totalCount ? (collectedCount / totalCount) * 100 : 0;

  function openDetail(row: Installment) {
    setDetailTarget(row);
    setDetailOpen(true);
  }

  return (
    <div className="mt-6 rounded-[10px] border border-line bg-card p-5 shadow-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-ink">Installment Schedule</h3>
          <p className="text-[13px] text-ink-secondary">
            {collectedCount} of {totalCount} collected · {formatINR(collectedAmt)} of {formatINR(totalAmt)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={handleSyncRocketPay}
            loading={syncing}
            leftIcon={<RefreshCw className={`h-3.5 w-3.5 ${syncing ? "animate-spin" : ""}`} />}
            title="Fetch and sync live installment schedule"
          >
            Sync Schedule
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/admin/installments?ref=${encodeURIComponent(mandate.refNo)}`)}
            rightIcon={<ArrowRight className="h-4 w-4" />}
          >
            View All
          </Button>
        </div>
      </div>

      <div className="mt-5">
        <div className="h-2 w-full overflow-hidden rounded-[999px] bg-[#F1F5F9]">
          <div className="h-full rounded-[999px] bg-success" style={{ width: `${progress}%` }} />
        </div>
        <p className="mt-1.5 text-xs text-ink-secondary">
          {nextDue ? `Next installment due on ${formatDueDate(nextDue.dueDate)}` : "No upcoming installments"}
        </p>
      </div>

      <div className="mt-5 overflow-x-auto">
        <table className="w-full min-w-[760px] text-left">
          <thead>
            <tr className="border-b border-line">
              {["Installment", "Gateway ID", "Due Date", "Amount", "Status", "Result", "Actions"].map((h, i) => (
                <th
                  key={h}
                  className={`px-4 py-3 text-xs font-semibold uppercase tracking-wide text-ink-secondary ${
                    i === 3 ? "text-right" : ""
                  }`}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-sm text-ink-secondary">
                  Loading schedule…
                </td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-sm text-ink-secondary">
                  No installments have been scheduled for this mandate yet.
                </td>
              </tr>
            )}
            {rows.map((r) => {
              const retryable = r.status === "FAILED";
              const skippable = r.status === "SCHEDULED";
              const isRefreshing = refreshingId === r.id;

              return (
                <tr key={r.id} className="border-b border-line last:border-0 hover:bg-page">
                  <td className="px-4 py-3.5">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-ink">
                          #{r.installmentNumber} of {r.totalInstallments}
                        </p>
                        {r.retryCount > 0 && (
                          <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-[#F1F5F9] px-2 py-0.5 text-[11px] font-semibold text-slate-600">
                            <RotateCcw className="h-2.5 w-2.5" />
                            Retry {r.retryCount}
                          </span>
                        )}
                      </div>
                      <span className="font-mono text-xs text-ink-muted">{r.installmentId}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3.5">
                    {r.rocketpayInstallmentId ? (
                      <div className="flex items-center gap-1.5">
                        <span
                          className="inline-flex items-center gap-1 font-mono text-xs text-primary-700 bg-primary-50 border border-primary-200 px-2 py-0.5 rounded whitespace-nowrap"
                          title={r.rocketpayInstallmentId}
                        >
                          <Zap className="h-3 w-3 text-primary-500 shrink-0" />
                          {r.rocketpayInstallmentId.slice(0, 8)}…{r.rocketpayInstallmentId.slice(-4)}
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            navigator.clipboard
                              .writeText(r.rocketpayInstallmentId!)
                              .then(() => toast.success("Gateway Installment ID copied"))
                              .catch(() => toast.error("Could not copy"));
                          }}
                          className="text-ink-muted hover:text-primary-600 p-0.5 cursor-pointer rounded hover:bg-slate-100"
                          title={`Copy full Gateway ID: ${r.rocketpayInstallmentId}`}
                        >
                          <Copy className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ) : (
                      <span className="text-xs text-[#94A3B8]">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3.5">
                    <p className="text-[13px] text-ink">{formatDueDate(r.dueDate)}</p>
                    <p className="text-xs text-ink-secondary">{dayName(r.dueDate)}</p>
                  </td>
                  <td className="px-4 py-3.5 text-right">
                    <span className="whitespace-nowrap text-sm font-medium text-ink">{formatINR(r.amount)}</span>
                  </td>
                  <td className="px-4 py-3.5">
                    <InstallmentStatusBadge status={r.status} size="sm" />
                  </td>
                  <td className="px-4 py-3.5">
                    {r.status === "COLLECTED_OFFLINE" ? (
                      <span className="text-xs text-[#1E40AF]">{r.offlineMode} offline</span>
                    ) : COLLECTED.includes(r.status) ? (
                      <span className="font-mono text-xs text-ink-secondary">{r.utrNumber}</span>
                    ) : r.status === "FAILED" ? (
                      <span className="text-xs text-danger">{r.failureReason}</span>
                    ) : (
                      <span className="text-[#94A3B8]">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3.5">
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => handleRefreshRow(r)}
                        disabled={isRefreshing}
                        className="flex h-8 w-8 items-center justify-center rounded-md text-primary-600 hover:bg-primary-50 cursor-pointer disabled:opacity-50"
                        aria-label="Sync with Gateway"
                        title="Sync with Gateway"
                      >
                        <RefreshCw className={`h-3.5 w-3.5 ${isRefreshing ? "animate-spin" : ""}`} />
                      </button>

                      {retryable && (
                        <button
                          type="button"
                          onClick={() => {
                            setRetryTarget(r);
                            setRetryOpen(true);
                          }}
                          className="flex h-8 items-center gap-1 px-2 rounded-md bg-amber-50 text-amber-700 hover:bg-amber-100 text-xs font-medium cursor-pointer"
                          title="Retry Schedule"
                        >
                          <RotateCcw className="h-3 w-3" />
                          Retry
                        </button>
                      )}

                      {skippable && (
                        <button
                          type="button"
                          onClick={() => {
                            setSkipTarget(r);
                            setSkipOpen(true);
                          }}
                          className="flex h-8 items-center gap-1 px-2 rounded-md bg-rose-50 text-rose-700 hover:bg-rose-100 text-xs font-medium cursor-pointer"
                          title="Skip Installment"
                        >
                          <CheckCircle2 className="h-3 w-3" />
                          Skip
                        </button>
                      )}

                      <button
                        type="button"
                        onClick={() => openDetail(r)}
                        className="flex h-8 w-8 items-center justify-center rounded-md text-ink-muted hover:bg-slate-100 hover:text-ink cursor-pointer"
                        aria-label="View installment details"
                        title="View Details & Inspect JSON"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <AdminInstallmentDetailModal
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        installment={detailTarget}
        mandate={mandate}
        onRefresh={handleRefreshRow}
        onRetry={(inst) => {
          setDetailOpen(false);
          setRetryTarget(inst);
          setRetryOpen(true);
        }}
        onSkip={(inst) => {
          setDetailOpen(false);
          setSkipTarget(inst);
          setSkipOpen(true);
        }}
        onCopyUtr={(utr) => {
          navigator.clipboard
            .writeText(utr)
            .then(() => toast.success("UTR copied to clipboard"))
            .catch(() => toast.error("Could not copy UTR"));
        }}
      />

      <RetryModal
        open={retryOpen}
        onClose={() => {
          setRetryOpen(false);
          setRetryTarget(null);
        }}
        installment={retryTarget}
        onConfirm={handleRetryConfirm}
        loading={retryLoading}
      />

      <SkipModal
        open={skipOpen}
        onClose={() => {
          setSkipOpen(false);
          setSkipTarget(null);
        }}
        installment={skipTarget}
        onConfirm={handleSkipConfirm}
        loading={skipLoading}
      />
    </div>
  );
}